import {
  MutableRefObject,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { TFunction } from 'i18next';
import DetectionVideoPlayer from '../DetectionVideoPlayer';

interface ResultVideoProps {
  t: TFunction<undefined>;
  url?: string;
  isLoadingVideo?: boolean;
  startTime?: number;
  endTime?: number;
  detectionFiles?: {
    damagesUrl?: string;
    detectionsUrl?: string;
  };
  showDetectionSwitchesAboveVideo?: boolean;
  showEquipmentDetectionSwitch?: boolean;
  showDamageDetectionSwitch?: boolean;
  chapterData?: Array<{
    startTime: number;
    endTime: number;
    title: string;
  }>;
  containerClassName?: string;
  videoClassName?: string;
  detectionVideoContainerClassName?: string;
  detectionSwitchesContainerClassName?: string;
  mediaNotFoundContainerClassName?: string;
}

export const ResultVideo = forwardRef<HTMLVideoElement, ResultVideoProps>(
  (
    {
      t,
      url,
      isLoadingVideo,
      startTime,
      endTime,
      detectionFiles,
      showDetectionSwitchesAboveVideo = true,
      showEquipmentDetectionSwitch = true,
      showDamageDetectionSwitch = true,
      chapterData,
      containerClassName,
      videoClassName,
      detectionVideoContainerClassName,
      detectionSwitchesContainerClassName,
      mediaNotFoundContainerClassName,
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLVideoElement>(null);
    const videoRef = (ref ??
      internalRef) as MutableRefObject<HTMLVideoElement | null>;

    // Plyr creates the <video> element asynchronously inside useVideoPlayer,
    // so we defer attaching playback listeners until onLoadedMetadata fires.
    const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);

    // Tracks whether watchPlayhead paused at endTime, so the next play press
    // restarts from startTime even if Plyr/the browser has moved the playhead
    // between pause and play (e.g. auto-seek to 0 when video.ended fires).
    const reachedEndRef = useRef(false);

    // If start === end (zero-length checkpoint), back up one second so the
    // frame is still visible instead of immediately pausing on the same time.
    const adjustedStartTime =
      startTime !== undefined && endTime !== undefined && startTime === endTime
        ? Math.max(0, startTime - 1)
        : startTime;

    const handleLoadedMetadata = useCallback(() => {
      const video = videoRef.current;
      if (video && adjustedStartTime !== undefined) {
        video.currentTime = adjustedStartTime;
      }
      setIsMetadataLoaded(true);
    }, [adjustedStartTime, videoRef]);

    useEffect(() => {
      if (!isMetadataLoaded) return undefined;
      const video = videoRef.current;
      if (!video) return undefined;
      if (adjustedStartTime === undefined && endTime === undefined) {
        return undefined;
      }

      const lowerBound = adjustedStartTime ?? 0;
      let rafId: number | null = null;

      // Poll on every animation frame:
      //  - pause the moment currentTime crosses endTime (without re-seeking
      //    every frame — re-seeking caused a visible jump back during normal
      //    playback);
      //  - if we previously paused at endTime and playback has resumed by any
      //    means (Plyr's play button, keyboard, programmatic .play()), seek
      //    back to startTime. Doing this here rather than in a 'play' listener
      //    is robust against Plyr re-seeking between pause and the play event.
      // The loop deliberately stays alive while paused: on initial play
      // `video.paused` can briefly still read true while buffering, which
      // would have killed an event-driven loop before playback even started.
      const watchPlayhead = () => {
        if (reachedEndRef.current && !video.paused) {
          video.currentTime = lowerBound;
          reachedEndRef.current = false;
        } else if (
          endTime !== undefined &&
          !video.paused &&
          !video.ended &&
          video.currentTime >= endTime
        ) {
          video.pause();
          reachedEndRef.current = true;
        }
        rafId = requestAnimationFrame(watchPlayhead);
      };

      const clampSeek = () => {
        if (endTime !== undefined && video.currentTime > endTime) {
          video.currentTime = endTime;
        } else if (video.currentTime < lowerBound) {
          video.currentTime = lowerBound;
        }
        // User scrubbed back inside the segment — they no longer want a
        // restart on the next play.
        if (endTime !== undefined && video.currentTime < endTime) {
          reachedEndRef.current = false;
        }
      };

      const handlePlay = () => {
        if (
          reachedEndRef.current ||
          (endTime !== undefined && video.currentTime >= endTime) ||
          video.ended
        ) {
          video.currentTime = lowerBound;
          reachedEndRef.current = false;
        }
      };

      video.addEventListener('seeking', clampSeek);
      video.addEventListener('play', handlePlay);
      rafId = requestAnimationFrame(watchPlayhead);

      return () => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        video.removeEventListener('seeking', clampSeek);
        video.removeEventListener('play', handlePlay);
      };
    }, [isMetadataLoaded, adjustedStartTime, endTime, videoRef]);

    return (
      <div className={twMerge('relative w-[560px] h-full', containerClassName)}>
        <DetectionVideoPlayer
          t={t}
          videoRef={videoRef}
          videoUrl={url}
          isLoadingVideo={isLoadingVideo}
          onLoadedMetadata={handleLoadedMetadata}
          showDetectionSwitchesAboveVideo={showDetectionSwitchesAboveVideo}
          showEquipmentDetectionSwitch={showEquipmentDetectionSwitch}
          showDamageDetectionSwitch={showDamageDetectionSwitch}
          detectionFiles={detectionFiles}
          chapterData={chapterData}
          videoContainerClassName={detectionVideoContainerClassName}
          videoClassName={videoClassName}
          detectionSwitchesContainerClassName={
            detectionSwitchesContainerClassName
          }
          detectionSwitchesToggleTextClassName="font-medium"
          detectionSwitchesContentClassName="gap-2.5"
          mediaNotFoundContainerClassName={twMerge(
            'h-[355px]',
            mediaNotFoundContainerClassName,
          )}
        />
      </div>
    );
  },
);
